import type { JSX } from 'react';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    OperatorParking,
    TerminalProvider,
} from '../../common/state/useParkingsFetch.ts';
import { Account } from '../../common/state/OperatorDataState.ts';
import { Public, VerifiedUser } from '@mui/icons-material';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';

export function providerToString(provider: TerminalProvider | null): string {
    switch (provider) {
        case TerminalProvider.VB:
            return 'Monocard';
        case TerminalProvider.PM:
            return 'Zeag';
        case TerminalProvider.SKIDATA_CLOUD:
        case TerminalProvider.SKIDATA_CLOUD_V7:
            return 'Skidata';
        case TerminalProvider.SB:
            return 'Scheidt & Bachmann';
        case TerminalProvider.DESIGNA:
            return 'PKE Designa';
        case TerminalProvider.HUB_PARKING:
            return 'HUB Parking';
        case TerminalProvider.CLOUD_CONNECTOR:
            return 'Cloud Connector';
        case null:
            return '-';
        default:
            return provider as string;
    }
}

const ParkingItem = ({
    parking,
    showAccount,
    account,
    onRowSelect,
}: {
    parking: OperatorParking;
    showAccount: boolean;
    account: OperatorDataState.Account | undefined;
    onRowSelect: () => void;
}): JSX.Element => {
    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={onRowSelect}>
            <TableCell>{parking.name}</TableCell>
            <TableCell>{parking.zipCode + ' ' + parking.city}</TableCell>
            <TableCell>{providerToString(parking.terminalProvider)}</TableCell>
            <TableCell>
                {parking.isPublic ? (
                    <Tooltip
                        title={
                            <Localized
                                de="Öffentlich"
                                fr="Public"
                                it="Pubblico"
                                en="Public"
                            />
                        }
                        placement="right"
                    >
                        <Public sx={{ height: 20 }} />
                    </Tooltip>
                ) : (
                    <Tooltip
                        title={
                            <Localized
                                de="Privat"
                                fr="Privé"
                                it="Privato"
                                en="Private"
                            />
                        }
                        placement="right"
                    >
                        <VerifiedUser sx={{ height: 20 }} />
                    </Tooltip>
                )}
            </TableCell>
            {showAccount && <TableCell>{account?.dtaText ?? EmDash}</TableCell>}
            <TableCell>{parking.zoneId}</TableCell>
        </TableRow>
    );
};

export function ParkingsList({ parkings }: { parkings: OperatorParking[] }) {
    const navigate = useNavigate();

    const accounts = useGetOperatorAccounts();
    const showAccount = accounts.length > 1;

    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Parkingname"
                                fr="Nom parking"
                                it="Nome parcheggio"
                                en="Parking name"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Ort"
                                fr="Localité"
                                it="Località"
                                en="Location"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Anlagetyp"
                                fr="Type d'installation"
                                it="Tipo d'impianto"
                                en="Installation type"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zutritt"
                                fr="Accès"
                                it="Accesso"
                                en="Access"
                            />
                        </TableCell>
                        {showAccount ? (
                            <TableCell>
                                <Localized
                                    de="Gruppe"
                                    fr="Groupe"
                                    it="Gruppo"
                                    en="Group"
                                />
                            </TableCell>
                        ) : null}
                        <TableCell>ID</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {parkings.map(parking => {
                        const account = accounts.find(
                            account =>
                                account.dtaAccountId === parking.dtaAccountId,
                        );
                        return (
                            <ParkingItem
                                key={parking.zoneId}
                                parking={parking}
                                account={account}
                                showAccount={showAccount}
                                onRowSelect={() =>
                                    navigate(`${parking.zoneId}`)
                                }
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export function useGetOperatorAccounts(): Account[] {
    const { storeState } = useStore(store => ({
        referenceData: OperatorDataState.get(store),
    }));

    const referenceData = storeState.referenceData.data;
    return referenceData ? referenceData.billingProperties.accounts : [];
}
